import { useCallback, useEffect, useRef, useState } from 'react';
import * as API from '../../services/api';
import { connectVoice, isWebRtcSupported } from './voiceWebRtc';
import { EMPTY_LIVE, reduceVoiceEvent } from './voiceLiveEvents';
import {
  VoiceError,
  VOICE_ERROR,
  isAbortError,
  micErrorCode,
  toVoiceErrorCode,
} from './voiceErrors';

export const VOICE_PHASE = {
  IDLE: 'idle',
  CREATING_SESSION: 'creating_session',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  LISTENING: 'listening',
  SPEAKING: 'speaking',
  PROCESSING: 'processing',
  ASSISTANT_SPEAKING: 'assistant_speaking',
  /** User said "hold on": the answer is kept until "continue" or "stop". */
  PAUSED: 'paused',
  STOPPING: 'stopping',
};

const LIVE_PHASES = new Set([
  VOICE_PHASE.CONNECTED,
  VOICE_PHASE.LISTENING,
  VOICE_PHASE.SPEAKING,
  VOICE_PHASE.PROCESSING,
  VOICE_PHASE.ASSISTANT_SPEAKING,
  VOICE_PHASE.PAUSED,
]);

export const isVoiceLive = (phase) => LIVE_PHASES.has(phase);

const TERMINAL_STATUS = new Set(['ended', 'closed', 'expired', 'failed', 'terminated', 'stopped']);
const STATUS_POLL_MS = 15000;
const DISCONNECT_GRACE_MS = 8000;
/** stopAfterAnswer: how long to wait for the spoken reply to start once the turn completes. */
const SPEECH_START_GRACE_MS = 2500;
/** stopAfterAnswer: upper bound in case the "stopped speaking" event never arrives. */
const SPEECH_MAX_MS = 60000;
/**
 * Silence after the assistant stops speaking before the answer counts as finished.
 * Longer than the pause while the next spoken chunk is prepared, so answers are not cut off.
 */
const ANSWER_SETTLE_MS = 3000;

const turnKey = (live) => `${live.turn ?? ''}|${live.question || live.userText}`;

const emptyResources = () => ({
  abort: null,
  stream: null,
  connection: null,
  audio: null,
  voice: null,
  sessionIds: null,
  projectId: null,
  token: null,
  timers: [],
  cleanups: [],
  botSpeaking: false,
  stopAfterSpeech: false,
  paused: false,
  muteSeq: 0,
  mutePending: 0,
  userMuted: false,
  autoMuted: false,
  autoMuteTimer: null,
  answerSettleTimer: null,
  completedTurn: null,
});

/**
 * Disabled tracks send silence, so nothing leaves the device even before the gateway confirms.
 * The mic is live only when neither the user nor the in-flight question has it muted.
 */
const syncTracks = (res) => {
  res.stream?.getAudioTracks().forEach((track) => {
    track.enabled = !(res.userMuted || res.autoMuted);
  });
};

/** Upper bound on the automatic mute in case the end of the turn is never reported. */
const AUTO_MUTE_MAX_MS = 120000;

/**
 * Voice session lifecycle for the chat composer.
 *
 * Project, token and conversation ids are always supplied by the caller at runtime.
 * Join credentials live only in a ref for the duration of the session.
 * With `stopAfterAnswer`, the session ends once a turn's answer has finished playing,
 * except while the user has paused it ("hold on") so that "continue" can still be heard.
 * With `muteWhileProcessing`, the mic is off from the moment a question is sent until its answer
 * starts playing (or the turn ends), so later speech cannot be added to that question.
 */
export default function useVoiceSession({
  projectId,
  token,
  sessionId,
  conversationId,
  locale,
  memoryEnabled,
  sttProvider,
  stopAfterAnswer = false,
  muteWhileProcessing = false,
  onSessionIds,
  onEnded,
  onTurnComplete,
  onQuestionSent,
  onAnswerPreview,
}) {
  const [phase, setPhase] = useState(VOICE_PHASE.IDLE);
  const [errorCode, setErrorCode] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [muted, setMutedState] = useState(false);
  const [autoMuted, setAutoMutedState] = useState(false);
  const [live, setLive] = useState(EMPTY_LIVE);
  const liveRef = useRef(EMPTY_LIVE);

  const resRef = useRef(emptyResources());
  const runIdRef = useRef(0);
  const mountedRef = useRef(true);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const callbacksRef = useRef({ onSessionIds, onEnded, onTurnComplete, onQuestionSent, onAnswerPreview });
  callbacksRef.current = { onSessionIds, onEnded, onTurnComplete, onQuestionSent, onAnswerPreview };
  const idsRef = useRef({ sessionId, conversationId, locale, memoryEnabled, sttProvider });
  idsRef.current = { sessionId, conversationId, locale, memoryEnabled, sttProvider };
  const stopAfterAnswerRef = useRef(stopAfterAnswer);
  stopAfterAnswerRef.current = stopAfterAnswer;
  const muteWhileProcessingRef = useRef(muteWhileProcessing);
  muteWhileProcessingRef.current = muteWhileProcessing;
  const stopRef = useRef(null);

  const safeSet = (fn) => {
    if (mountedRef.current) fn();
  };

  const applyUserMute = (res, value) => {
    res.userMuted = value;
    syncTracks(res);
    safeSet(() => setMutedState(value));
  };

  const applyAutoMute = (res, value) => {
    if (res.autoMuted === value) return;
    if (!value && res.autoMuteTimer) {
      clearTimeout(res.autoMuteTimer);
      res.autoMuteTimer = null;
    }
    res.autoMuted = value;
    syncTracks(res);
    safeSet(() => setAutoMutedState(value));
  };

  /** Release every resource synchronously. Returns what was held (for callers that need ids). */
  const teardown = useCallback(({ endRemote = true } = {}) => {
    const r = resRef.current;
    resRef.current = emptyResources();
    runIdRef.current += 1;

    try {
      r.abort?.abort();
    } catch {
      /* ignore */
    }
    r.timers.forEach((t) => {
      clearTimeout(t);
      clearInterval(t);
    });
    r.cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        /* ignore */
      }
    });
    r.connection?.close();
    r.stream?.getTracks().forEach((t) => t.stop());
    if (r.audio) {
      try {
        r.audio.pause();
      } catch {
        /* jsdom / already stopped */
      }
      r.audio.srcObject = null;
    }
    if (endRemote && r.voice && r.token && r.projectId) {
      API.endVoiceSession(r.token, r.projectId, r.voice.sessionId, r.voice.joinToken);
    }
    liveRef.current = EMPTY_LIVE;
    if (mountedRef.current) {
      setLive(EMPTY_LIVE);
      setMutedState(false);
      setAutoMutedState(false);
    }
    return r;
  }, []);

  const fail = useCallback(
    (code) => {
      teardown();
      safeSet(() => {
        setAudioBlocked(false);
        setPhase(VOICE_PHASE.IDLE);
        setErrorCode(code);
      });
    },
    [teardown],
  );

  const playRemote = useCallback((audio) => {
    let p;
    try {
      p = audio.play();
    } catch {
      p = null;
    }
    if (p && typeof p.then === 'function') {
      p.then(() => safeSet(() => setAudioBlocked(false))).catch((err) => {
        if (err?.name === 'NotAllowedError') safeSet(() => setAudioBlocked(true));
        else if (err?.name !== 'AbortError') safeSet(() => setErrorCode(VOICE_ERROR.AUDIO));
      });
    }
  }, []);

  const start = useCallback(async () => {
    if (phaseRef.current !== VOICE_PHASE.IDLE) return;
    if (!projectId || !token) return;
    if (!isWebRtcSupported()) {
      setErrorCode(VOICE_ERROR.UNSUPPORTED);
      return;
    }

    teardown({ endRemote: false });
    const runId = runIdRef.current;
    const stale = () => runIdRef.current !== runId || !mountedRef.current;
    const res = resRef.current;
    res.abort = new AbortController();
    res.projectId = projectId;
    res.token = token;

    setErrorCode(null);
    setAudioBlocked(false);
    setPhase(VOICE_PHASE.CREATING_SESSION);

    let stage = 'create';
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: false,
        });
      } catch (err) {
        throw new VoiceError(micErrorCode(err), err);
      }
      if (stale()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      res.stream = stream;

      const { sessionId: sid, conversationId: cid, locale: loc, memoryEnabled: mem, sttProvider: stt } =
        idsRef.current;
      const resp = await API.createVoiceSession(
        token,
        projectId,
        { sessionId: sid, conversationId: cid, locale: loc, memoryEnabled: mem, sttProvider: stt },
        res.abort.signal,
      );
      const data = resp?.data || {};
      const voice = data.voice || {};
      if (!voice.session_id || !voice.join_token || (data.project_id && data.project_id !== projectId)) {
        throw new VoiceError(VOICE_ERROR.SERVER);
      }
      const voiceRef = { sessionId: voice.session_id, joinToken: voice.join_token };
      if (stale()) {
        API.endVoiceSession(token, projectId, voiceRef.sessionId, voiceRef.joinToken);
        return;
      }
      res.voice = voiceRef;
      if (voice.muted === true) applyUserMute(res, true);
      stage = 'signal';
      callbacksRef.current.onSessionIds?.({
        sessionId: data.session_id || null,
        conversationId: data.conversation_id || null,
      });
      res.sessionIds = { sessionId: data.session_id || null, conversationId: data.conversation_id || null };

      setPhase(VOICE_PHASE.CONNECTING);

      const audio = new Audio();
      audio.autoplay = true;
      audio.muted = false;
      res.audio = audio;

      let disconnectTimer = null;
      const onConnectionState = (state) => {
        if (stale()) return;
        if (state === 'connected') {
          if (disconnectTimer) clearTimeout(disconnectTimer);
          disconnectTimer = null;
          setPhase((p) => (p === VOICE_PHASE.CONNECTING || p === VOICE_PHASE.CONNECTED ? VOICE_PHASE.LISTENING : p));
        } else if (state === 'failed') {
          fail(VOICE_ERROR.WEBRTC);
        } else if (state === 'disconnected' && !disconnectTimer) {
          disconnectTimer = setTimeout(() => {
            if (!stale()) fail(VOICE_ERROR.WEBRTC);
          }, DISCONNECT_GRACE_MS);
          res.timers.push(disconnectTimer);
        }
      };

      const connection = await connectVoice({
        localStream: stream,
        iceServers: voice.ice_servers,
        sendOffer: async (offer) => {
          const r = await API.sendVoiceOffer(
            token,
            projectId,
            voiceRef.sessionId,
            voiceRef.joinToken,
            { ...offer, restart_pc: false },
            res.abort.signal,
          );
          return r?.data;
        },
        onRemoteStream: (remote) => {
          if (stale()) return;
          audio.srcObject = remote;
          playRemote(audio);
        },
        onConnectionState,
        onEvent: (event) => {
          if (stale()) return;
          const prevLive = liveRef.current;
          const result = reduceVoiceEvent(prevLive, event);
          const turnInfo = (live, extras = {}) => ({
            timedOut: live.errorCode === 'EAP_TIMEOUT',
            userText: live.userText,
            assistantText: live.assistantSentences.join(' ').trim(),
            discarded: live.command === 'stop',
            ...extras,
          });
          const completeTurn = (live, { armStop }) => {
            res.completedTurn = turnKey(live);
            callbacksRef.current.onTurnComplete?.(res.sessionIds, turnInfo(live));
            if (stopAfterAnswerRef.current && !res.stopAfterSpeech) {
              res.stopAfterSpeech = true;
              armStop();
            }
          };
          // Next question started before the prior turn was flushed — keep prior Q&A in the chat.
          const nextLive = result.live;
          if (
            res.sessionIds &&
            nextLive !== prevLive &&
            prevLive.userText &&
            (prevLive.assistantSentences.length > 0 || prevLive.done) &&
            prevLive.command !== 'stop' &&
            res.completedTurn !== turnKey(prevLive) &&
            (
              (nextLive.turn != null && prevLive.turn != null && nextLive.turn !== prevLive.turn) ||
              (Boolean(nextLive.userText) &&
                nextLive.userText !== prevLive.userText &&
                nextLive.assistantSentences.length === 0 &&
                !nextLive.done)
            )
          ) {
            completeTurn(prevLive, { armStop: () => {} });
          }
          if (result.live !== liveRef.current) {
            liveRef.current = result.live;
            setLive(result.live);
          }
          const autoStop = () => {
            if (!stale() && !res.paused) stopRef.current?.();
          };
          const armAutoStop = () => {
            res.timers.push(setTimeout(() => {
              if (!res.botSpeaking) autoStop();
            }, SPEECH_START_GRACE_MS));
            res.timers.push(setTimeout(autoStop, SPEECH_MAX_MS));
          };
          if (result.muted !== null && !res.mutePending) applyUserMute(res, result.muted);
          // Speech while the question is being answered would be merged into it and re-sent.
          if (result.questionSent && muteWhileProcessingRef.current && !res.autoMuted) {
            applyAutoMute(res, true);
            res.autoMuteTimer = setTimeout(() => {
              if (!stale()) applyAutoMute(res, false);
            }, AUTO_MUTE_MAX_MS);
            res.timers.push(res.autoMuteTimer);
          } else if (
            res.autoMuted &&
            (result.phase === VOICE_PHASE.ASSISTANT_SPEAKING || result.turnComplete || result.turnFailed)
          ) {
            applyAutoMute(res, false);
          }
          // Show the question in chat as soon as EAP receives it; poll so the full answer
          // appears before (or while) TTS starts reading it aloud.
          if (result.questionSent && res.sessionIds && result.live.userText) {
            callbacksRef.current.onQuestionSent?.(res.sessionIds, {
              userText: result.live.userText,
            });
          }
          if (
            result.answerPreview &&
            res.sessionIds &&
            result.live.command !== 'stop'
          ) {
            // Bind preview to the EAP question for this turn — not interim speech that replaced userText.
            const previewFor = result.live.question || result.live.userText;
            const preview = result.live.assistantSentences.join(' ').trim();
            if (previewFor && preview) {
              callbacksRef.current.onAnswerPreview?.(res.sessionIds, {
                userText: previewFor,
                assistantText: preview,
              });
            }
          }
          res.paused = result.live.paused;
          if (result.command && result.command !== 'hold' && res.stopAfterSpeech) armAutoStop();
          // The user cut the answer short; show what EAP saved so far without waiting for the turn to end.
          if ((result.command === 'stop' || result.command === 'hold') && res.sessionIds) {
            callbacksRef.current.onTurnComplete?.(res.sessionIds, turnInfo(result.live));
          }
          if (result.phase) {
            const next = result.phase;
            setPhase((p) => (isVoiceLive(p) ? next : p));
            const wasSpeaking = res.botSpeaking;
            res.botSpeaking = next === VOICE_PHASE.ASSISTANT_SPEAKING;
            if (res.botSpeaking) {
              clearTimeout(res.answerSettleTimer);
            } else if (wasSpeaking) {
              // Answers are spoken in chunks with short silences between them, so the answer only
              // counts as finished once speech has stayed stopped for the whole settle window.
              clearTimeout(res.answerSettleTimer);
              res.answerSettleTimer = setTimeout(() => {
                if (stale() || res.botSpeaking || res.paused) return;
                if (res.stopAfterSpeech) {
                  autoStop();
                  return;
                }
                // Fallback when the voice service never reports the turn as complete.
                const live = liveRef.current;
                if (!res.sessionIds || !live.assistantSentences.length || live.command === 'stop') return;
                if (res.completedTurn === turnKey(live)) return;
                completeTurn(live, { armStop: autoStop });
              }, ANSWER_SETTLE_MS);
              res.timers.push(res.answerSettleTimer);
            }
          } else if (result.command && !result.live.paused) {
            setPhase((p) => (p === VOICE_PHASE.PAUSED ? VOICE_PHASE.LISTENING : p));
          }
          if (result.turnComplete && res.sessionIds && res.completedTurn !== turnKey(result.live)) {
            clearTimeout(res.answerSettleTimer);
            completeTurn(result.live, { armStop: armAutoStop });
          }
        },
      });
      if (stale()) {
        connection.close();
        return;
      }
      res.connection = connection;

      stream.getAudioTracks().forEach((track) => {
        const onTrackEnded = () => {
          if (!stale()) fail(VOICE_ERROR.MIC_UNAVAILABLE);
        };
        track.addEventListener?.('ended', onTrackEnded);
        res.cleanups.push(() => track.removeEventListener?.('ended', onTrackEnded));
      });

      const onUnload = () => teardown();
      window.addEventListener('pagehide', onUnload);
      window.addEventListener('beforeunload', onUnload);
      res.cleanups.push(() => {
        window.removeEventListener('pagehide', onUnload);
        window.removeEventListener('beforeunload', onUnload);
      });

      const poll = setInterval(async () => {
        if (stale()) return;
        try {
          const s = await API.getVoiceSessionStatus(
            token,
            projectId,
            voiceRef.sessionId,
            voiceRef.joinToken,
            res.abort.signal,
          );
          const state = String(s?.data?.state || s?.data?.status || '').toLowerCase();
          if (!stale() && TERMINAL_STATUS.has(state)) fail(VOICE_ERROR.VOICE_SESSION_EXPIRED);
          else if (!stale() && typeof s?.data?.muted === 'boolean' && !res.mutePending) {
            applyUserMute(res, s.data.muted);
          }
        } catch (err) {
          if (stale() || isAbortError(err)) return;
          const status = err?.response?.status;
          if (status === 404) fail(VOICE_ERROR.VOICE_SESSION_EXPIRED);
          else if (status === 401) fail(VOICE_ERROR.SESSION_EXPIRED);
        }
      }, STATUS_POLL_MS);
      res.timers.push(poll);

      const connectedNow = connection.pc?.connectionState === 'connected';
      setPhase((p) =>
        p === VOICE_PHASE.CONNECTING ? (connectedNow ? VOICE_PHASE.LISTENING : VOICE_PHASE.CONNECTED) : p,
      );
    } catch (err) {
      if (stale() || isAbortError(err)) return;
      fail(toVoiceErrorCode(err, stage));
    }
  }, [projectId, token, teardown, fail, playRemote]);

  /** @param {{ notify?: boolean }} [opts] notify=false skips onEnded (caller is switching conversations). */
  const stop = useCallback(async (opts) => {
    const notify = opts?.notify !== false;
    const current = phaseRef.current;
    if (current === VOICE_PHASE.IDLE || current === VOICE_PHASE.STOPPING) return;
    setPhase(VOICE_PHASE.STOPPING);
    const held = teardown({ endRemote: false });
    if (held.voice && held.token && held.projectId) {
      await API.endVoiceSession(held.token, held.projectId, held.voice.sessionId, held.voice.joinToken);
    }
    safeSet(() => {
      setAudioBlocked(false);
      setPhase(VOICE_PHASE.IDLE);
    });
    if (notify && held.sessionIds && mountedRef.current) callbacksRef.current.onEnded?.(held.sessionIds);
  }, [teardown]);
  stopRef.current = stop;

  const resumeAudio = useCallback(() => {
    const audio = resRef.current.audio;
    if (audio) playRemote(audio);
  }, [playRemote]);

  const clearError = useCallback(() => setErrorCode(null), []);

  /**
   * Mute: the mic track is disabled at once and the gateway is told to drop the caller's audio.
   * If unmuting fails on the gateway the UI stays muted, matching what the server enforces.
   */
  const setMuted = useCallback(async (next) => {
    const res = resRef.current;
    if (!res.voice || !res.stream || !isVoiceLive(phaseRef.current)) return;
    const runId = runIdRef.current;
    const stale = () => runIdRef.current !== runId || !mountedRef.current;
    const seq = ++res.muteSeq;
    res.mutePending += 1;
    applyUserMute(res, next);
    try {
      const r = await API.setVoiceMicrophone(
        res.token,
        res.projectId,
        res.voice.sessionId,
        res.voice.joinToken,
        next,
        res.abort?.signal,
      );
      if (stale() || seq !== res.muteSeq) return;
      applyUserMute(res, typeof r?.data?.muted === 'boolean' ? r.data.muted : next);
    } catch (err) {
      if (stale() || isAbortError(err)) return;
      const status = err?.response?.status;
      if (status === 404) {
        fail(VOICE_ERROR.VOICE_SESSION_EXPIRED);
        return;
      }
      if (status === 401) {
        fail(VOICE_ERROR.SESSION_EXPIRED);
        return;
      }
      if (seq !== res.muteSeq) return;
      if (!next) applyUserMute(res, true);
      setErrorCode(VOICE_ERROR.MUTE_FAILED);
    } finally {
      if (runIdRef.current === runId) res.mutePending -= 1;
    }
  }, [fail]);

  /** Result of looking for EAP's saved answer after the voice service gave up on this turn. */
  const markAnswerCheck = useCallback((userText, found) => {
    const current = liveRef.current;
    if (current.answerCheck !== 'pending' || current.userText !== userText) return;
    liveRef.current = { ...current, answerCheck: found ? 'found' : 'missing' };
    setLive(liveRef.current);
  }, []);

  // Project switch (and unmount): end the previous project's session.
  useEffect(() => {
    return () => {
      teardown();
      safeSet(() => {
        setAudioBlocked(false);
        setPhase(VOICE_PHASE.IDLE);
      });
    };
  }, [projectId, teardown]);

  // Auth expiry / logout.
  useEffect(() => {
    if (!token && phaseRef.current !== VOICE_PHASE.IDLE) fail(VOICE_ERROR.SESSION_EXPIRED);
  }, [token, fail]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardown();
    };
  }, [teardown]);

  return {
    phase,
    errorCode,
    audioBlocked,
    muted,
    autoMuted,
    live,
    start,
    stop,
    resumeAudio,
    clearError,
    setMuted,
    markAnswerCheck,
  };
}
